import { useQuery } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"

export type User = {
  id: string
  fullName: string
  email: string
  role: string // free-form from backend enum app_role
  team?: string | null
  sub_teams?: string[]
  isActive: boolean
  dsp_id?: string | null
  person?: string | null
  status?: string | null
  areas?: string[] | null
}

export type UsersFilters = {
  roles?: string[]
  team?: string | null
  dsp?: "with" | "without" | "any"
  // search and filter extensions
  q?: string // search by full_name/email
  isActive?: boolean
  status?: string | null
  subTeams?: string[] // overlaps
  // pagination
  limit?: number // page size
  page?: number // 1-based
  offset?: number // explicit offset overrides page
}

export type UsersResult = {
  users: User[]
  total: number
  limit: number
  offset: number
  page: number
  pageSize: number
}

export const useUsers = (filters: UsersFilters) => {
  return useQuery({
    queryKey: ["users", filters],
    queryFn: async (): Promise<UsersResult> => {
      try {
        const { data, error } = await supabase.functions.invoke("read-users", {
          body: filters,
        })

        if (error) {
          throw new Error(error.message)
        }

        return {
          users: data.users || [],
          total: typeof data.total === "number" ? data.total : (data.users?.length ?? 0),
          limit: typeof data.limit === "number" ? data.limit : (filters.limit ?? 100),
          offset: typeof data.offset === "number" ? data.offset : (filters.offset ?? 0),
          page: typeof data.page === "number" ? data.page : (filters.page ?? 1),
          pageSize: typeof data.pageSize === "number" ? data.pageSize : (filters.limit ?? 100),
        }
      } catch (error) {
        console.error("Error fetching users:", error)
        throw error
      }
    },
  })
}
